import { useState } from "react";
import moment from "moment-timezone";
import { ChevronLeft, ChevronRight, Eye, RefreshCw } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  getDocumentDispatchPreview,
  type DocumentDeliveryStatus,
  type DocumentDispatchListItemDto,
  type DocumentDispatchListQuery,
  type DocumentPreparationStatus,
  useHideAgtechContractDocumentInstance,
  useListNightlyDocumentDispatches,
  useSendDocumentDispatch,
} from "@/lib/api/otcDocuments";
import {
  HideAgtechContractDocumentInstanceRequestValidation,
  SendDocumentDispatchRequestValidation,
} from "@/lib/validation/otcDocuments";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  documentDispatchRowBgColor,
  documentDispatchStatusLegend,
} from "./documentDispatchRowColors";
import { DocumentDispatchEversignStatusReportDialog } from "./DocumentDispatchEversignStatusReportDialog";

type QueueTab = "automated" | "sent" | "archived";
type PreviewDocumentType = "sent" | "signed";

const PAGE_SIZE = 100;

const tabFilters: Record<QueueTab, Partial<DocumentDispatchListQuery>> = {
  automated: { preset: "automated_queued" },
  sent: { queue_status: "submitted" },
  archived: { visibility_status: "hidden" },
};

function rowIdentity(row: DocumentDispatchListItemDto): string {
  return `${customerName(row) || "Unknown customer"}, contract ${row.AgtechContractInfo?.contractNumber || "—"}, amendment: ${row.AgtechContractInfo?.amendmentNumber || "-"}`;
}

function customerName(row: DocumentDispatchListItemDto): string {
  const info = row.AgtechContractInfo;
  return info?.Client?.long_name
    ?? info?.ToClient?.long_name
    ?? info?.SoldToClient?.long_name
    ?? info?.PurchasedFromClient?.long_name
    ?? info?.FromClient?.long_name
    ?? "";
}

function formatDocumentStatus(
  status: DocumentPreparationStatus | DocumentDeliveryStatus,
): string {
  const prettyStatuses: Record<DocumentPreparationStatus | DocumentDeliveryStatus, string> = {
    queued: "Queued",
    processing: "Processing",
    ready: "Ready",
    failed: "Failed",
    not_queued: "Not Queued",
    submitted: "Submitted",
  };
  return prettyStatuses[status];
}

function formatExtractedDate(value: string | null): string {
  if (!value) return "";
  const parsed = moment.tz(value, "M/D/YYYY", CommonDestinationTimezone);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
}

function extractedDateColumn(
  id: string,
  header: string,
  value: (row: DocumentDispatchListItemDto) => string | null,
): DataTableColumn<DocumentDispatchListItemDto> {
  return {
    id,
    header,
    accessorFn: (row: DocumentDispatchListItemDto) => value(row),
    columnFilterType: "date",
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (rawValue: unknown) => rawValue
      ? moment.tz(rawValue as string, "M/D/YYYY", CommonDestinationTimezone).format("YYYY/MM/DD")
      : "",
    filterParseDateFn: (rawValue: unknown) => rawValue
      ? moment.tz(rawValue as string, "M/D/YYYY", CommonDestinationTimezone)
      : null,
    cell: ({ row }) => formatExtractedDate(value(row.original)),
  };
}

export function NightlyDocumentsQueuePage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<QueueTab>("automated");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [actionsRow, setActionsRow] = useState<DocumentDispatchListItemDto | null>(null);
  const [sendRow, setSendRow] = useState<DocumentDispatchListItemDto | null>(null);
  const [hideRow, setHideRow] = useState<DocumentDispatchListItemDto | null>(null);
  const query: DocumentDispatchListQuery = {
    feature_type: "agtech_email_contract",
    page,
    page_size: PAGE_SIZE,
    ...tabFilters[activeTab],
  };
  const listResult = useListNightlyDocumentDispatches(query);
  const listData = listResult.data?.success ? listResult.data.data : null;
  const rows = listData?.items ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const previewDocument = async (
    row: DocumentDispatchListItemDto,
    documentType: PreviewDocumentType,
  ): Promise<void> => {
    if (previewLoadingId !== null) return;
    setPreviewLoadingId(row.id);
    try {
      const result = await getDocumentDispatchPreview(row.id, documentType);
      if (!result.success) {
        toast({
          title: "Failed to preview document",
          description: result.message || "The document is unavailable.",
          variant: "destructive",
        });
        return;
      }
      setPreview({
        url: result.data.url,
        title: `${documentType === "signed" ? "Signed" : "Sent"} nightly contract ${row.AgtechContractInfo?.contractNumber ?? ""}`.trim(),
      });
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const columns: DataTableColumn<DocumentDispatchListItemDto>[] = [
    ...(activeTab !== "archived"
      ? [{
          id: "preview",
          header: "Preview",
          enableFiltering: false,
          enableSorting: false,
          disableRowClick: true,
          cell: ({ row }: { row: { original: DocumentDispatchListItemDto } }) => {
            const signedAvailable = row.original.signing?.signedDocument?.available === true;
            const sentAvailable = row.original.signing?.originalDocument?.available === true;
            const documentType: PreviewDocumentType | null = activeTab === "sent" && signedAvailable
              ? "signed"
              : sentAvailable
                ? "sent"
                : null;

            return documentType ? (
              <Button
                size="sm"
                variant="outline"
                disabled={previewLoadingId !== null}
                onClick={() => previewDocument(row.original, documentType)}
              >
                <Eye className="mr-1 h-3 w-3" />
                {documentType === "signed" ? "Preview Signed" : "Preview Sent"}
              </Button>
            ) : null;
          },
        }]
      : []),
    {
      id: "actions",
      header: "Actions",
      enableFiltering: false,
      enableSorting: false,
      disableRowClick: true,
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => setActionsRow(row.original)}>
          Actions
        </Button>
      ),
    },
    {
      id: "customer",
      accessorFn: (row: DocumentDispatchListItemDto) => customerName(row),
      header: "Customer",
      size: 220,
    },
    {
      id: "trader",
      accessorFn: (row: DocumentDispatchListItemDto) => row.AgtechContractInfo?.trader ?? "",
      header: "Trader",
    },
    {
      id: "contract_number",
      accessorFn: (row: DocumentDispatchListItemDto) => row.AgtechContractInfo?.contractNumber ?? "",
      header: "Contract #",
    },
    {
      id: "amendment_number",
      accessorFn: (row: DocumentDispatchListItemDto) => row.AgtechContractInfo?.amendmentNumber ?? "",
      header: "Amendment #",
    },
    extractedDateColumn(
      "relevant_date",
      "Date",
      (row: DocumentDispatchListItemDto) => row.AgtechContractInfo?.amendmentDate ?? row.AgtechContractInfo?.contractDate ?? null,
    ),
    {
      id: "location",
      accessorFn: (row: DocumentDispatchListItemDto) => row.AgtechContractInfo?.location ?? "",
      header: "Location",
    },
    {
      id: "contract_type",
      accessorFn: (row: DocumentDispatchListItemDto) => row.AgtechContractInfo?.contractType ?? "",
      header: "Contract Type",
    },
    {
      id: "preparation_status",
      accessorFn: (row: DocumentDispatchListItemDto) => row.preparationStatus,
      header: "Preparation",
      cell: ({ row }) => formatDocumentStatus(row.original.preparationStatus),
    },
    {
      id: "delivery_status",
      accessorFn: (row: DocumentDispatchListItemDto) => row.deliveryStatus,
      header: "Delivery",
      cell: ({ row }) => formatDocumentStatus(row.original.deliveryStatus),
    },
    ...(activeTab === "sent"
      ? [
        {
          id: "eversign_status",
          accessorFn: (row: DocumentDispatchListItemDto) => row.signing?.lastStatus ?? "",
          header: "Eversign Status",
        },
        {
          id: "actual_send_method",
          accessorFn: (row: DocumentDispatchListItemDto) => row.actualSendMethod ?? "",
          header: "Send Method",
        },
      ]
      : []),
    ...(activeTab === "sent" || activeTab === "archived"
      ? [{
          id: "sent_at",
          accessorFn: (row: DocumentDispatchListItemDto) => row.sentAt,
          header: "Sent At",
          columnFilterType: "date" as const,
          filterDateTimezone: CommonDestinationTimezone,
          filterDisplayFn: (rawValue: unknown) => rawValue
            ? moment.tz(rawValue as string, CommonDestinationTimezone).format("YYYY/MM/DD")
            : "",
          filterParseDateFn: (rawValue: unknown) => rawValue
            ? moment.tz(rawValue as string, CommonDestinationTimezone)
            : null,
          cell: ({ row }: { row: { original: DocumentDispatchListItemDto } }) => row.original.sentAt
            ? moment.tz(row.original.sentAt, CommonDestinationTimezone).format("MM/DD/YYYY h:mm A")
            : "",
          size: 190,
        }]
      : []),
    ...(activeTab === "archived"
      ? [{
          id: "hidden_reason",
          accessorFn: (row: DocumentDispatchListItemDto) => row.hiddenReason ?? "",
          header: "Hidden Reason",
          size: 240,
        }]
      : []),
    {
      id: "error",
      accessorFn: (row: DocumentDispatchListItemDto) => row.errorMessage ?? "",
      header: "Error",
      size: 240,
    },
    {
      id: "created_at",
      accessorFn: (row: DocumentDispatchListItemDto) => row.createdAt,
      header: "Created At",
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (rawValue: unknown) => moment
        .tz(rawValue as string, CommonDestinationTimezone)
        .format("YYYY/MM/DD"),
      filterParseDateFn: (rawValue: unknown) => moment
        .tz(rawValue as string, CommonDestinationTimezone),
      cell: ({ row }) => moment
        .tz(row.original.createdAt, CommonDestinationTimezone)
        .format("MM/DD/YYYY h:mm A"),
      size: 190,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Document Queue</p>
        <h1 className="text-2xl font-bold">Nightly Contracts</h1>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={activeTab}
          onValueChange={(value: string) => {
            setActiveTab(value as QueueTab);
            setPage(1);
          }}
        >
          <TabsList>
            <TabsTrigger value="automated">Automated</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          <DocumentDispatchEversignStatusReportDialog
            featureType="agtech_email_contract"
            title="Generate Nightly Signing Report"
            triggerLabel="Generate Nightly Signing Report"
          />
          <Button variant="outline" disabled={listResult.isFetching} onClick={() => listResult.refetch()}>
            <RefreshCw className={`mr-2 h-4 w-4 ${listResult.isFetching ? "animate-spin" : ""}`} />
            {listResult.isFetching ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>
      {!listResult.isLoading && listResult.data && !listResult.data.success && (
        <p className="text-sm text-destructive">
          {listResult.data.message || "Failed to load nightly contract dispatches."}
        </p>
      )}
      <DataTable
        stickyHeaders
        tableClassName="min-h-[calc(40vh-100px)] max-h-[calc(100vh-360px)] overflow-y-scroll"
        defaultSorting={[{ id: "created_at", desc: true }]}
        data={rows}
        columns={columns}
        rowBgColor={activeTab === "automated" || activeTab === "sent" ? documentDispatchRowBgColor : undefined}
        legend={activeTab === "automated" || activeTab === "sent" ? documentDispatchStatusLegend : undefined}
        enableSorting
        enableMultiSort
        enableFiltering
        enableMultiFilter
        enableColumnVisibility
        enableColumnResizing
        enableColumnPinning
        filterStyle="select"
        filterMode="internal"
        defaultCondensed
        emptyMessage={listResult.isLoading ? "Loading..." : "No nightly contract dispatches found"}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {total === 0
            ? "0 results"
            : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 1 || listResult.isFetching}
            onClick={() => setPage((current: number) => current - 1)}
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <span className="text-sm">Page {page} of {totalPages}</span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages || listResult.isFetching}
            onClick={() => setPage((current: number) => current + 1)}
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ActionsDialog
        row={actionsRow}
        activeTab={activeTab}
        previewLoadingId={previewLoadingId}
        onClose={() => setActionsRow(null)}
        onPreview={previewDocument}
        onSend={setSendRow}
        onHide={setHideRow}
      />
      <SendDialog row={sendRow} onClose={() => setSendRow(null)} />
      <HideDialog row={hideRow} onClose={() => setHideRow(null)} />
      <Dialog open={preview !== null} onOpenChange={(open: boolean) => { if (!open) setPreview(null); }}>
        <DialogContent className="flex h-[85vh] max-w-[85vw] flex-col">
          <DialogHeader><DialogTitle>{preview?.title || "Document Preview"}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-hidden">
            {preview && <iframe src={preview.url} title={preview.title} className="h-full w-full border-0" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActionsDialog({
  row,
  activeTab,
  previewLoadingId,
  onClose,
  onPreview,
  onSend,
  onHide,
}: {
  row: DocumentDispatchListItemDto | null;
  activeTab: QueueTab;
  previewLoadingId: string | null;
  onClose: () => void;
  onPreview: (row: DocumentDispatchListItemDto, documentType: PreviewDocumentType) => Promise<void>;
  onSend: (row: DocumentDispatchListItemDto) => void;
  onHide: (row: DocumentDispatchListItemDto) => void;
}) {
  const sentAvailable = row?.signing?.originalDocument?.available === true;
  const signedAvailable = row?.signing?.signedDocument?.available === true;

  return (
    <Dialog open={row !== null} onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Document Actions</DialogTitle>
          <DialogDescription>{row ? rowIdentity(row) : ""}</DialogDescription>
        </DialogHeader>
        {row && (
          <div className="flex flex-wrap gap-2">
            {sentAvailable && (
              <Button
                size="sm"
                variant="outline"
                disabled={previewLoadingId !== null}
                onClick={() => { onClose(); onPreview(row, "sent"); }}
              >
                <Eye className="mr-1 h-3 w-3" /> Preview Sent
              </Button>
            )}
            {signedAvailable && (
              <Button
                size="sm"
                variant="outline"
                disabled={previewLoadingId !== null}
                onClick={() => { onClose(); onPreview(row, "signed"); }}
              >
                <Eye className="mr-1 h-3 w-3" /> Preview Signed
              </Button>
            )}
            {activeTab === "automated" && row.sendEligible && (
              <Button size="sm" onClick={() => { onClose(); onSend(row); }}>Send</Button>
            )}
            {activeTab !== "archived" && row.hiddenAt === null && row.AgtechContractDocumentInstance !== null && (
              <Button size="sm" variant="destructive" onClick={() => { onClose(); onHide(row); }}>Hide</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendDialog({ row, onClose }: { row: DocumentDispatchListItemDto | null; onClose: () => void }) {
  const { toast } = useToast();
  const sendDispatch = useSendDocumentDispatch("agtech_email_contract");
  const [sendType, setSendType] = useState<"test" | "live" | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    const parsed = SendDocumentDispatchRequestValidation.safeParse({
      queue_entry_id: row?.id,
      is_test_send: sendType === "test",
    });
    if (!parsed.success || !sendType || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await sendDispatch(parsed.data);
      if (!result.success) {
        toast({
          title: "Failed to queue contract send",
          description: result.message || "Failed to queue the contract send.",
          variant: "destructive",
        });
      } else {
        toast({
          title: sendType === "test" ? "Test send queued" : "Live send queued",
          description: result.message,
        });
        setSendType("");
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={(open: boolean) => { if (!open && !isSubmitting) { setSendType(""); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Nightly Contract</DialogTitle>
          <DialogDescription>
            {row ? rowIdentity(row) : ""}. Choose whether to perform a sandbox test send or a live customer send.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Send type</Label>
          <Select value={sendType} onValueChange={(value: string) => setSendType(value as "test" | "live")} disabled={isSubmitting}>
            <SelectTrigger><SelectValue placeholder="Choose test or live send" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="test">Test send — deliver to internal ops</SelectItem>
              <SelectItem value="live">Live send — deliver to customer</SelectItem>
            </SelectContent>
          </Select>
          {sendType === "live" && <p className="font-medium text-destructive">This is a live send to the customer.</p>}
          {sendType === "test" && <p className="font-medium">This is a sandbox test send.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={onClose}>Cancel</Button>
          <Button disabled={!sendType || isSubmitting} onClick={submit}>{isSubmitting ? "Queueing..." : "Confirm Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HideDialog({ row, onClose }: { row: DocumentDispatchListItemDto | null; onClose: () => void }) {
  const { toast } = useToast();
  const hideInstance = useHideAgtechContractDocumentInstance();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    const parsed = HideAgtechContractDocumentInstanceRequestValidation.safeParse({
      instance_id: row?.AgtechContractDocumentInstance?.id,
      reason,
    });
    if (!parsed.success || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await hideInstance(parsed.data);
      if (!result.success) {
        toast({
          title: "Failed to hide contract",
          description: result.message || "Failed to hide the contract.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Contract hidden", description: result.message });
        setReason("");
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={(open: boolean) => { if (!open && !isSubmitting) { setReason(""); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Hide Nightly Contract</DialogTitle>
          <DialogDescription>
            {row ? rowIdentity(row) : ""}. Preparation: {row?.preparationStatus || "—"}; delivery: {row?.deliveryStatus || "—"}. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="nightly-document-hide-reason">Reason</Label>
          <Textarea
            id="nightly-document-hide-reason"
            value={reason}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setReason(event.target.value)}
            disabled={isSubmitting}
            placeholder="Enter a reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={!reason.trim() || isSubmitting} onClick={submit}>
            {isSubmitting ? "Hiding..." : "Hide"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
